import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import {
    Button,
    WeezHeader,
    CopyToClipboardLink,
    Card,
    Icon,
    Table,
    Tooltip,
    ToastContext,
    Message,
    Modal,
    ContentCard,
    TableCellDropdown,
    FilterSetController,
    Filter,
    SubCard,
    Status,
    Dropdown,
    DropdownItem,
    Paginator,
    CheckboxLabel
} from '@weezevent/nacre';
import SortDropdown from '../../../components/sortDropdown';
import { usePermissions } from '../../../user/permissions';
import Subscription from '../../../models/subscription';
import SubscriptionLinkModel from '../../../models/subscriptionLink';
import { useTranslation } from 'react-i18next';
import LoadingTable from '../../../components/loadingTable';
import DeleteModal from './components/deleteModal';
import Styles from './managementRulesList.module.css';
import { SyncButton } from '../../../components/sync-button/sync-button';
import { InfoTextToolTipMemo } from '../../attendee/list/components/attendee-row/attendee-row';
import DeletionModal from '../../../components/deletionModal';
import SubscriptionLinkModal from '../linkModal';
import Event from '../../../models/eventModel';
import Loading from '../../../components/loading';
import ResendTicketModal from '../resendTicketModal';

function HeaderRow({ isPush, ordering, onChangeOrdering }) {
    const { t } = useTranslation();

    const getLabelsForType = React.useCallback(type => {
        const mapping = {
            string: {
                ascending: t('ticket.common.ordering.A-Z'),
                descending: t('ticket.common.ordering.Z-A')
            },
            date: {
                ascending: t('ticket.accounting.filter.increasing'),
                descending: t('ticket.accounting.filter.descending')
            }
        };
        return mapping[type] || {};
    }, []);

    // Helper method to generate ordering items
    const getOrderingItems = React.useCallback(
        (column, type) => {
            const labels = getLabelsForType(type);
            return Object.entries(labels).map(([direction, label]) => (
                <DropdownItem.Icons
                    key={`${column}-${direction}`}
                    item={label}
                    onClick={() => onChangeOrdering(direction === 'ascending' ? column : `-${column}`)}
                    checked={ordering === (direction === 'ascending' ? column : `-${column}`)}
                />
            ));
        },
        [ordering, onChangeOrdering, getLabelsForType]
    );

    const HeaderCell = ({ label, size = 'width-x-medium' }) => <th className={Styles[size]}>{label}</th>;

    return (
        <Table.Tr isHeader>
            <SortDropdown
                name="name"
                ordering={ordering}
                items={getOrderingItems('name', 'string')}
                label={t('ticket.webservice.management-rules.header.name')}
                className={Styles['width-x-medium']}
            />
            <HeaderCell label={t('ticket.webservice.management-rules.header.destination')} />
            {!isPush && (
                <SortDropdown
                    name="last_position"
                    ordering={ordering}
                    items={getOrderingItems('last_position', 'date')}
                    label={t('ticket.webservice.arrayLabel.lastSync')}
                    className={Styles['width-x-medium']}
                />
            )}
            {!isPush && (
                <SortDropdown
                    name="next_update"
                    ordering={ordering}
                    items={getOrderingItems('next_update', 'date')}
                    label={t('ticket.webservice.arrayLabel.nextSync')}
                    className={Styles['width-x-medium']}
                />
            )}
            {isPush && <HeaderCell label={t('ticket.webservice.management-rules.header.conditions')} />}
            <HeaderCell label={t('ticket.webservice.management-rules.header.attendees')} size="width-x-small" />
            {!isPush && <HeaderCell label={t('ticket.webservice.list.statusArrayHeader')} size="width-x-small" />}
            <HeaderCell label={t('ticket.webservice.management-rules.header.actions')} size="width-x-mini" />
        </Table.Tr>
    );
}

HeaderRow.propTypes = {
    isPush: PropTypes.bool,
    ordering: PropTypes.string,
    onChangeOrdering: PropTypes.func.isRequired
};
function TableRow({ link, onSelectManagementRule, organizationId, subscriptionId, isPush, setModalState, onSelectManagementRuleRepushScans, permissions }) {
    const { t } = useTranslation();
    const history = useHistory();

    const goToEvent = React.useMemo(() => {
        return (
            <a href={`/ticket/O${organizationId}/E${link.event_id}/dashboard`} target="_blank" rel="noopener noreferrer">
                <Icon name="external-link" />
            </a>
        );
    }, [link, organizationId]);

    const displayCondition = React.useMemo(() => {
        if (!link.ticket_filter) {
            return '-';
        }
        for (const [key, value] of Object.entries(link.ticket_filter)) {
            return `${key}: ${value}`;
        }
    }, [link]);

    const linkStatus = React.useMemo(() => {
        if (link.lock !== null) {
            const minutes = Math.floor((Date.now() - Date.parse(link.lock)) / (1000 * 60));
            return (
                <Tooltip title={t('ticket.webservice.list.status-processing-tooltip', { time: minutes })} triggerOnHover>
                    <Status text={t('ticket.webservice.list.statusList.processing')} color="orange" responsive />
                </Tooltip>
            );
        }

        const statusMap = {
            hidden: { text: t('ticket.webservice.list.statusList.finish'), color: '' },
            valid: { text: t('ticket.webservice.list.statusList.actif'), color: 'green' },
            default: { text: t('ticket.webservice.list.statusList.inactif'), color: 'red' }
        };

        const { text, color } = statusMap[link.status] || statusMap.default;

        return <Status text={text} color={color} responsive />;
    }, [link]);

    const formatDate = value => (value ? new Date(value).toLocaleDateString() : '-');
    const formatTime = value => (value ? new Date(value).toLocaleTimeString() : '-');

    const eventCell = (
        <div>
            <div className={Styles['flex-table-column']}>
                {link.event_name || link.event_id ? (
                    <>
                        <div className={Styles['table-column-event']}>{link.event_name || link.event_id}</div>

                        <span className={Styles['external-link']} onClick={e => e.stopPropagation()}>
                            {goToEvent}
                        </span>
                    </>
                ) : (
                    <>
                        <span className={Styles['activate-import-esktop-only']}>
                            <Button
                                primary
                                inverted
                                onClick={e => {
                                    e.stopPropagation();
                                    setModalState({ type: 'link', object: link });
                                }}
                                label={t('ticket.webservice.list.link')}
                            />
                        </span>
                        <span className={Styles['activate-import-mobile-only']}>
                            <Icon name="link" onClick={() => setModalState({ type: 'link', object: link })} />
                        </span>
                    </>
                )}
            </div>

            {link.event_id && (
                <>
                    <div className={Styles['flex-table-column']}>
                        {t('ticket.common.group')}:<div>{link.category_name || link.category_id || '-'}</div>
                    </div>

                    <div className={Styles['flex-table-column']}>
                        {t('ticket.common.sessions')}:<div>{link.date_id || '-'}</div>
                    </div>
                </>
            )}
        </div>
    );

    return (
        <Table.Tr
            onClick={() => {
                history.push(`/ticket/O${organizationId}/webservices/${subscriptionId}/attendees?subscription_link_id__in=${link.id}`);
            }}
        >
            <Table.Td className={Styles['width-x-medium']}>
                <InfoTextToolTipMemo title={link.name} subtitle={String(link.id)} tooltipCopy={Boolean(link.name || link.id)} />
            </Table.Td>
            <Table.Td className={Styles['width-x-medium']}>{eventCell}</Table.Td>
            {!isPush && (
                <>
                    <Table.Td>
                        {link.last_position ? <InfoTextToolTipMemo title={formatDate(link.last_position)} subtitle={formatTime(link.last_position)} tooltipCopy={true} /> : '-'}
                    </Table.Td>
                    <Table.Td>
                        {link.next_update ? <InfoTextToolTipMemo title={formatDate(link.next_update)} subtitle={formatTime(link.next_update)} tooltipCopy={true} /> : '-'}
                    </Table.Td>
                </>
            )}
            {isPush && <Table.Td className={Styles['width-x-small']}>{displayCondition || '-'}</Table.Td>}
            <Table.Td className={Styles['width-x-small']}>{link.tickets_count || '-'}</Table.Td>
            {!isPush && <Table.Td>{linkStatus}</Table.Td>}
            <TableCellDropdown className={Styles['width-x-mini']}>
                {isPush && (
                    <>
                        <Dropdown.Item item="Renvoyer les participants" onClick={() => setModalState({ type: 'resend', object: link })} />
                        <Dropdown.Item item={t('ticket.common.action.delete')} deleteItem onClick={() => onSelectManagementRule(link)} />
                    </>
                )}

                {!isPush && !link.event_id && <Dropdown.Item item={t('ticket.webservice.list.link')} onClick={() => setModalState({ type: 'link', object: link })} />}

                {!isPush && link.event_id && (
                    <>
                        <Dropdown.Item item={t('ticket.webservice.list.unLink')} onClick={() => setModalState({ type: 'remove', object: link })} />
                        <Dropdown.Item item={t('ticket.webservice.list.resetLink')} onClick={() => setModalState({ type: 'reset', object: link })} />
                        {link.lock && <Dropdown.Item item={t('ticket.webservice.list.unlockLink')} onClick={() => setModalState({ type: 'unlock', object: link })} />}
                    </>
                )}
                {!isPush && permissions?.isAdmin() && <Dropdown.Item item={t('ticket.webservice.repushScans')} onClick={() => onSelectManagementRuleRepushScans(link)} />}
            </TableCellDropdown>
        </Table.Tr>
    );
}

TableRow.propTypes = {
    link: PropTypes.object,
    onSelectManagementRule: PropTypes.func.isRequired,
    onSelectManagementRuleRepushScans: PropTypes.func.isRequired,
    organizationId: PropTypes.string.isRequired,
    subscriptionId: PropTypes.string.isRequired,
    isPush: PropTypes.bool.isRequired,
    setModalState: PropTypes.func.isRequired
};

function createQuery(params) {
    const { status, isLinked, isNotLinked, ...query } = params;
    if (status && status.size > 0) {
        query.status__in = Array.from(status).join(',');
    }
    if (isLinked && !isNotLinked) {
        query.is_linked = true;
    }
    if (!isLinked && isNotLinked) {
        query.is_linked = false;
    }
    return query;
}

export default function ManagementRules({ organizationId, subscriptionId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const permissions = usePermissions();
    const toast = React.useContext(ToastContext);

    const [queryParams, setQueryParams] = React.useState({
        search: '',
        ordering: 'name',
        status: new Set(['valid']),
        offset: 0,
        limit: 100,
        isLinked: true,
        isNotLinked: true
    });

    const [selectedManagementRule, setSelectedManagementRule] = React.useState(null);
    const [selectedManagementRuleRepushScans, setSelectedManagementRuleRepushScans] = React.useState(null);
    const [openModalResyncSub, setOpenModalResyncSub] = React.useState(false);
    const [modalState, setModalState] = React.useState({
        type: null, // 'reset' | 'remove' | 'link' | 'unlock'
        object: null
    });

    const [subscription, loadingSubscription] = Subscription.useApiModel(
        {
            cache: false,
            allow_cache: false,
            organizationId: organizationId,
            id: subscriptionId
        },
        [organizationId, subscriptionId]
    );

    // eslint-disable-next-line no-unused-vars
    const [subscriptionLinks, loadingSubscriptionLinks, syncSubscriptionLinks, subscriptionLinksError, subscriptionLinksTotalCount] = SubscriptionLinkModel.useApiModel(
        {
            cache: false,
            allow_cache: false,
            organizationId: organizationId,
            subscriptionId: subscriptionId,
            query: createQuery(queryParams)
        },
        [organizationId, subscriptionId, queryParams]
    );

    const [events, loadingEvent] = Event.useApiModel(
        {
            organizationId,
            query: {
                publication_status__in: 'published,draft'
            }
        },
        [organizationId]
    );

    const handleChangePage = React.useCallback(value => {
        setQueryParams(prev => ({ ...prev, offset: prev.limit * value }));
    }, []);

    const handleChangeOrdering = React.useCallback(value => {
        // Reset pagination when changing ordering
        // We should reset the offset and currentPage to always show the first page with the actual server data
        setQueryParams(prev => ({ ...prev, ordering: value, offset: 0 }));
    }, []);

    const handleChangeFilters = React.useCallback(({ name, value }) => {
        // Reset pagination when changing query params
        // We should reset the offset and currentPage to always show the first page with the actual server data
        setQueryParams(prev => ({ ...prev, [name]: value, offset: 0 }));
    }, []);

    const resyncSubscription = React.useCallback(() => {
        Subscription.push_tickets({ organizationId, subscriptionId: subscription.pk })
            .then(() => {
                toast.success(t('ticket.webservice.management-rules.toast_sync_success'));
                setOpenModalResyncSub(false);
            })
            .catch(() => {
                toast.error(t('ticket.common.toast_error'));
            });
    }, [subscription, organizationId, toast]);

    const onCloseModal = React.useCallback(() => {
        setSelectedManagementRule(null);
    }, []);

    const unlinkSubscriptionLink = React.useCallback(
        link => {
            link.unlink({ organizationId })
                .then(() => {
                    toast.success(t('ticket.webservice.subscriptionLink.unlinkSuccess'));
                    syncSubscriptionLinks({ allow_cache: false, cache: false });
                })
                .catch(() => {
                    toast.error(t('ticket.webservice.subscriptionLink.unlinkFailure'));
                })
                .finally(() => {
                    // Close the modal by reseting modal state
                    setModalState({ type: null, object: null });
                });
        },
        [organizationId, syncSubscriptionLinks]
    );

    const resetSubscriptionLink = React.useCallback(
        link => {
            link.reset({ organizationId })
                .then(() => {
                    toast.success(t('ticket.webservice.subscriptionLink.resetSuccess'));
                    syncSubscriptionLinks({ allow_cache: false, cache: false });
                })
                .catch(() => {
                    toast.error(t('ticket.webservice.subscriptionLink.resetFailure'));
                })
                .finally(() => {
                    // Close the modal by reseting modal state
                    setModalState({ type: null, object: null });
                });
        },
        [organizationId, syncSubscriptionLinks]
    );

    const unlockSubscriptionLink = React.useCallback(
        link => {
            link.unlock({ organizationId })
                .then(() => {
                    toast.success(t('ticket.webservice.subscriptionLink.unlockSuccess'));
                    syncSubscriptionLinks({ allow_cache: false, cache: false });
                })
                .catch(() => {
                    toast.error(t('ticket.webservice.subscriptionLink.unlockFailure'));
                })
                .finally(() => {
                    // Close the modal by reseting modal state
                    setModalState({ type: null, object: null });
                });
        },
        [organizationId, syncSubscriptionLinks]
    );

    // Configuration object for deletion/reset/unlock modals.
    const deletionConfig = {
        reset: {
            onAccept: obj => resetSubscriptionLink(obj),
            labelTitle: t('ticket.webservice.subscriptionLink.resetTitleModal'),
            labelButton: t('ticket.webservice.subscriptionLink.reset'),
            labelBody: t('ticket.webservice.subscriptionLink.resetBodyModal'),
            icon: 'sync'
        },
        remove: {
            onAccept: obj => unlinkSubscriptionLink(obj),
            labelTitle: t('ticket.webservice.list.modalRemove.title'),
            labelButton: t('ticket.common.action.delete'),
            labelBody: t('ticket.webservice.list.modalRemove.body'),
            icon: 'sync'
        },
        unlock: {
            onAccept: obj => unlockSubscriptionLink(obj),
            labelTitle: t('ticket.webservice.list.modalUnlock.title'),
            labelButton: t('ticket.common.action.continue'),
            labelBody: t('ticket.webservice.list.modalUnlock.body'),
            icon: 'sync'
        }
    };

    const options = [
        { key: 'valid', text: t('ticket.webservice.optionLabel.actifOption'), name: 'valid' },
        { key: 'trouble', text: t('ticket.webservice.optionLabel.inactifOption'), name: 'trouble' },
        { key: 'hidden', text: t('ticket.webservice.optionLabel.finishOption'), name: 'hidden' }
    ];

    const initialFilterValues = React.useMemo(() => {
        return !loadingSubscription && !subscription?.is_push() ? { status: new Set(['valid']) } : {};
    }, [loadingSubscription, subscription]);

    const repushScans = React.useCallback(() => {
        if (!selectedManagementRuleRepushScans) return;

        Subscription.repushScans({ organizationId, subscriptionId, linkIds: [selectedManagementRuleRepushScans.id] })
            .then(() => {
                toast.success(t('ticket.webservice.management-rules.toast_sync_success'));
                setSelectedManagementRuleRepushScans(null);
            })
            .catch(() => {
                toast.error(t('ticket.common.toast_error'));
            });
    }, [selectedManagementRuleRepushScans, organizationId, subscriptionId]);

    if (loadingEvent || loadingSubscription) {
        return <Loading />;
    }

    return (
        <div className={Styles['management-rules-container']}>
            <Modal
                title={t('ticket.webservice.management-rules.modal-sync-sub.title')}
                onClose={() => setOpenModalResyncSub(false)}
                open={openModalResyncSub}
                submitButton={<Button primary onClick={() => resyncSubscription()} label={t('ticket.common.action.confirm')} />}
                cancelButton={<Button onClick={() => setOpenModalResyncSub(false)} label={t(`ticket.common.action.cancel`)} />}
            >
                <Message title={t('ticket.webservice.management-rules.modal-sync-sub.warning-card.title')} type="danger">
                    <p>{t('ticket.webservice.management-rules.modal-sync-sub.warning-card.text')}</p>
                </Message>
            </Modal>
            <Modal
                title={t('ticket.webservice.management-rules.modal-repush-scans.title')}
                onClose={() => setSelectedManagementRuleRepushScans(null)}
                open={selectedManagementRuleRepushScans}
                submitButton={<Button primary onClick={() => repushScans()} label={t('ticket.common.action.confirm')} />}
                cancelButton={<Button onClick={() => setSelectedManagementRuleRepushScans(null)} label={t(`ticket.common.action.cancel`)} />}
            >
                <Message title={t('ticket.webservice.management-rules.modal-repush-scans.warning-card.title')} type="danger">
                    <p>{t('ticket.webservice.management-rules.modal-repush-scans.warning-card.text')}</p>
                </Message>
            </Modal>
            {['reset', 'remove', 'unlock'].includes(modalState.type) && (
                <DeletionModal
                    objectToDelete={modalState.object}
                    onClose={() => setModalState({ type: null, object: null })}
                    onAccept={() => deletionConfig[modalState.type].onAccept(modalState.object)}
                    labelTitle={deletionConfig[modalState.type].labelTitle}
                    labelButton={deletionConfig[modalState.type].labelButton}
                    labelBody={deletionConfig[modalState.type].labelBody}
                    icon={deletionConfig[modalState.type].icon}
                />
            )}

            {modalState.type === 'link' && (
                <SubscriptionLinkModal
                    events={events}
                    organizationId={organizationId}
                    onError={() => toast.error(t('ticket.webservice.subscriptionLink.error'))}
                    subscriptionLink={modalState.object}
                    onClose={() => setModalState({ type: null, object: null })}
                    onSuccess={() => {
                        toast.success(t('ticket.webservice.subscriptionLink.linked'));
                        setModalState({ type: null, object: null });
                        syncSubscriptionLinks({ allow_cache: false, cache: false });
                    }}
                />
            )}
            {modalState.type === 'resend' && (
                <ResendTicketModal
                    show={true}
                    onClose={() => setModalState({ type: null, object: null })}
                    organizationId={organizationId}
                    subscriptionId={subscriptionId}
                    subscriptionLink={modalState.object}
                />
            )}
            <WeezHeader
                title={t('ticket.webservice.management-rules.title')}
                rightComponent={[
                    <Tooltip key="sync-tooltip" triggerOnHover position={'bottom'} title={t('ticket.webservice.management-rules.sync-tooltip')}>
                        <SyncButton sync={() => setOpenModalResyncSub(true)} />
                    </Tooltip>,
                    subscription?.is_push() && (
                        <Button
                            key="top-cta-form"
                            primary
                            label={
                                <div className={Styles['header-button']}>
                                    <Icon name="plus" />
                                    <div>{t('ticket.webservice.management-rules.cta-add')}</div>
                                </div>
                            }
                            onClick={() => history.push(`/ticket/O${organizationId}/webservices/${subscriptionId}/subscription_links/new`)}
                        />
                    )
                ]}
            />
            {subscription?.is_push() && (
                <>
                    <Message type="warning">{t('ticket.webservice.management-rules.warning-message')}</Message>
                    <SubCard title={subscription?.name}>
                        <div className={Styles['card-url']}>
                            <CopyToClipboardLink
                                copyOnIconClick
                                delay={1000}
                                tooltipMini
                                value={`${window.weezConfig.api.apiUrl}/ticket-imports/organizations/${organizationId}/hooks/${subscription?.hook_uuid}`}
                                tooltip={t('ticket.common.action.copy')}
                            >
                                <>
                                    <span className={Styles['url-title']}>{t('ticket.webservice.management-rules.url')}</span>
                                    <span
                                        className={Styles['url']}
                                    >{`${window.weezConfig.api.apiUrl}/ticket-imports/organizations/${organizationId}/hooks/${subscription?.hook_uuid}`}</span>
                                </>
                            </CopyToClipboardLink>
                        </div>
                    </SubCard>
                </>
            )}
            <Card>
                <ContentCard>
                    <FilterSetController
                        total={subscriptionLinksTotalCount}
                        isLoadingTotal={loadingSubscriptionLinks}
                        labelTotal={t('ticket.webservice.management-rules.total', { count: subscriptionLinksTotalCount })}
                        labelReset={t('ticket.common.action.reset')}
                        search={{
                            value: queryParams.search || '',
                            placeholder: t('ticket.common.action.search'),
                            onChange: (synth, value) => {
                                handleChangeFilters({ name: 'search', value: value || '' });
                            },
                            handleReset: (synth, value) => {
                                handleChangeFilters({ name: 'search', value: '' });
                            }
                        }}
                        onChange={values => {
                            handleChangeFilters({ name: 'status', value: values.status });
                        }}
                        initialValues={initialFilterValues}
                    >
                        {!subscription?.is_push() ? (
                            <Filter
                                name="status"
                                labelSelectAll={t('ticket.common.action.select-all')}
                                labelName={t('ticket.accounting.filter.statusLabel')}
                                multiple
                                options={options}
                                index="1"
                            />
                        ) : null}
                    </FilterSetController>
                    {!subscription?.is_push() && (
                        <div className={Styles['filter-checkboxes']}>
                            <span className={Styles['filter-boxes-label']}>{`${t('ticket.common.action.display')}: `}</span>

                            <CheckboxLabel
                                name="linked"
                                label={t('ticket.webservice.list.checkboxlink')}
                                checked={queryParams.isLinked}
                                onChange={() => handleChangeFilters({ name: 'isLinked', value: !queryParams.isLinked })}
                            />

                            <CheckboxLabel
                                name="unlinked"
                                label={t('ticket.webservice.list.checkboxUnlink')}
                                checked={queryParams.isNotLinked}
                                onChange={() => handleChangeFilters({ name: 'isNotLinked', value: !queryParams.isNotLinked })}
                            />
                        </div>
                    )}
                </ContentCard>
                <Table useNew className={Styles['attendees_table']}>
                    <Table.THead>
                        <HeaderRow isPush={subscription?.is_push()} ordering={queryParams.ordering} onChangeOrdering={handleChangeOrdering} />
                    </Table.THead>
                    {!loadingSubscription && !loadingSubscriptionLinks && (
                        <>
                            <DeleteModal
                                subscription={selectedManagementRule}
                                show={Boolean(selectedManagementRule)}
                                onClose={onCloseModal}
                                sync={syncSubscriptionLinks}
                                organizationId={organizationId}
                                subscriptionId={subscriptionId}
                            />
                            <Table.TBody>
                                {subscriptionLinks.map((link, index) => (
                                    <TableRow
                                        key={index}
                                        link={link}
                                        isPush={subscription?.is_push()}
                                        subscriptionId={subscriptionId}
                                        organizationId={organizationId}
                                        onSelectManagementRule={setSelectedManagementRule}
                                        onSelectManagementRuleRepushScans={setSelectedManagementRuleRepushScans}
                                        history={history}
                                        setModalState={setModalState}
                                        permissions={permissions}
                                    />
                                ))}
                            </Table.TBody>
                        </>
                    )}
                </Table>
                <LoadingTable loading={loadingSubscription || loadingSubscriptionLinks} />
                {!loadingSubscriptionLinks && !subscriptionLinksTotalCount && (
                    <Card.EmptyState
                        title={t('ticket.webservice.management-rules.no-rules')}
                        subtitle={t('ticket.webservice.management-rules.no-rules-info')}
                        icon={<Icon name="user" />}
                        labelButton={t('ticket.webservice.management-rules.no-rules-cta')}
                        onClick={() => history.push(`/ticket/O${organizationId}/webservices/${subscriptionId}/subscription_links/new`)}
                    />
                )}
                <div style={{ display: 'flex', justifyContent: 'center', margin: '10px 0' }}>
                    <Paginator
                        pageSize={queryParams.limit}
                        totalCount={subscriptionLinksTotalCount}
                        currentPage={Math.floor(Number(queryParams.offset) / Number(queryParams.limit))}
                        onPageChange={handleChangePage}
                    />
                </div>
            </Card>
        </div>
    );
}

ManagementRules.propTypes = {
    organizationId: PropTypes.string.isRequired,
    subscriptionId: PropTypes.string.isRequired
};
